/**
 * Seller — Catálogo (lista de productos con precio de venta, sin costos).
 * Permite exportar PDF para compartir con clientes por WhatsApp.
 */
import { useEffect, useState } from "react";
import { api } from "../../App";
import { Card } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Package, Search, Download } from "lucide-react";
import { PageLoader } from "../../components/Spinner";
import { EmptyState } from "../../components/EmptyState";
import { toast } from "sonner";

const fmt = (n) =>
  new Intl.NumberFormat("es-CR", {
    style: "currency",
    currency: "CRC",
    maximumFractionDigits: 0,
  }).format(Number(n) || 0);

export default function SellerProducts() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/seller/products");
        setProducts(data || []);
      } catch {
        setProducts([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const exportPdf = async () => {
    setExporting(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/seller/catalog/export-pdf`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!res.ok) throw new Error("fail");
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "catalogo.pdf");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Catálogo descargado — compartilo por WhatsApp");
    } catch {
      toast.error("Error al exportar catálogo");
    } finally {
      setExporting(false);
    }
  };

  if (loading) return <PageLoader label="Cargando catálogo…" testid="seller-products-loader" />;

  const filtered = products.filter((p) => {
    const t = q.trim().toLowerCase();
    if (!t) return true;
    return (
      (p.codigo || "").toLowerCase().includes(t) ||
      (p.descripcion || "").toLowerCase().includes(t)
    );
  });

  return (
    <div data-testid="seller-products-page">
      <div className="flex items-center justify-between mb-5 gap-3">
        <div>
          <h2 className="text-2xl font-bold tracking-tight" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
            Catálogo
          </h2>
          <p className="text-slate-500 text-sm mt-0.5">
            {products.length} producto{products.length === 1 ? "" : "s"} disponibles
          </p>
        </div>
        <Button
          onClick={exportPdf}
          disabled={exporting || products.length === 0}
          className="btn-primary rounded-sm"
          data-testid="seller-export-catalog-button"
        >
          <Download className="h-4 w-4 mr-1.5" />
          {exporting ? "Generando…" : "PDF"}
        </Button>
      </div>

      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Buscar por código o descripción…"
          className="pl-9 rounded-sm"
          data-testid="seller-products-search"
        />
      </div>

      {filtered.length === 0 ? (
        <EmptyState
          icon={Package}
          title={q ? "Sin resultados" : "Sin productos en el catálogo"}
          description={q ? "Probá con otro término." : "Tu admin todavía no cargó productos."}
          testid="seller-products-empty"
        />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
          {filtered.map((p) => (
            <Card key={p.id} className="bg-white border border-slate-200 rounded-sm p-3 flex items-center justify-between" data-testid="seller-product-card">
              <div className="min-w-0">
                <div className="mono text-[11px] text-slate-500 uppercase tracking-wider">{p.codigo}</div>
                <div className="font-medium text-slate-900 truncate">{p.descripcion}</div>
              </div>
              <div className="text-right ml-3 flex-shrink-0">
                <div className="text-base font-bold text-[#002FA7]" style={{ fontFamily: "Cabinet Grotesk" }}>
                  {fmt(p.precio_venta)}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
